import React, { useState, useEffect } from 'react';
import { Card, CardBody, Input, Button } from "@material-tailwind/react";
import { FaTimes, FaCheck, FaBell } from 'react-icons/fa';

const PaymentTypeEditModal = ({ 
    isOpen, 
    onClose, 
    paymentType, 
    type, // 'car' or 'driver'
    onSave,
    user,
    carPaymentTypes = [],
    driverPaymentTypes = []
}) => {
    const [formData, setFormData] = useState({
        type: '',
        short_desc: '',
        long_desc: '',
        sign: '',
        driver_post: false,
        driver_pay_type: '',
        sub_type: '',
        reversal_type: '',
        def: false,
        notify: false,
        can_split: true,
        car_post: false,
        car_post_type: ''
    });

    useEffect(() => {
        if (paymentType) {
            setFormData({
                type: paymentType.type || '',
                short_desc: paymentType.short_desc || '',
                long_desc: paymentType.long_desc || '',
                sign: paymentType.sign || '',
                driver_post: paymentType.driver_post || false,
                driver_pay_type: paymentType.driver_pay_type || '',
                sub_type: paymentType.sub_type || '',
                reversal_type: paymentType.reversal_type || '',
                def: paymentType.def || false,
                notify: paymentType.notify || false,
                can_split: paymentType.can_split !== false,
                car_post: paymentType.car_post || false,
                car_post_type: paymentType.car_post_type || ''
            });
        }
    }, [paymentType]);

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Validate required fields
        if (!formData.type.trim() || !formData.short_desc.trim() || !formData.long_desc.trim() || !formData.sign.trim()) {
            alert('Type, Short Description, Long Description, and Sign are required.');
            return;
        }

        // Validate reversal_type if provided
        if (formData.reversal_type.trim()) {
            const revType = formData.reversal_type.trim().toUpperCase();
            if (type === 'car') {
                const exists = carPaymentTypes.some(t => t.type === revType);
                if (!exists) {
                    alert(`Invalid Reversal Type: '${revType}' does not exist in car payment types.`);
                    return;
                }
            } else if (type === 'driver') {
                const exists = driverPaymentTypes.some(t => t.type === revType);
                if (!exists) {
                    alert(`Invalid Reversal Type: '${revType}' does not exist in driver payment types.`);
                    return;
                }
            }
        }

        // Prepare data based on type
        const submitData = {
            type: formData.type.toUpperCase(),
            short_desc: formData.short_desc.toUpperCase(),
            long_desc: formData.long_desc,
            sign: formData.sign,
            sub_type: formData.sub_type.toUpperCase(),
            reversal_type: formData.reversal_type.toUpperCase(),
            def: formData.def,
            notify: formData.notify,
            edit_by: user.username
        };

        // Add car-specific fields
        if (type === 'car') {
            submitData.driver_post = formData.driver_post;
            submitData.driver_pay_type = formData.driver_pay_type.toUpperCase();
            submitData.can_split = formData.can_split;
        }

        // Add driver-specific fields
        if (type === 'driver') {
            submitData.car_post = formData.car_post;
            submitData.car_post_type = formData.car_post_type.toUpperCase();
        }

        await onSave(paymentType.type, submitData);
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <Card className="w-full max-w-4xl max-h-[90vh]">
            
                
                <CardBody className="p-4 flex-1 overflow-y-auto">
                <Button
                        variant="text"
                        color="white"
                        onClick={onClose}
                        className="p-1 flex-shrink-0 ml-2"
                    >
                        <FaTimes />
                    </Button>
                    <form onSubmit={handleSubmit} className="space-y-3">
                        {/* Basic Information */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <Input
                                type="text"
                                label="Type Code"
                                value={formData.type}
                                onChange={e => handleInputChange('type', e.target.value.slice(0, 10))}
                                maxLength={10}
                                required
                            />
                            <Input
                                type="text"
                                label="Short Description"
                                value={formData.short_desc}
                                onChange={e => handleInputChange('short_desc', e.target.value)}
                                required
                            />
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <Input
                                type="text"
                                label="Long Description"
                                value={formData.long_desc}
                                onChange={e => handleInputChange('long_desc', e.target.value)}
                                required
                            />
                            <Input
                                type="text"
                                label="Sign (+/-)"
                                value={formData.sign}
                                onChange={e => handleInputChange('sign', e.target.value.slice(0, 1))}
                                maxLength={1}
                                required
                            />
                        </div>

                        {/* Car-specific fields */}
                        {type === 'car' && (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <Input
                                    type="text"
                                    label="Driver Pay Type"
                                    value={formData.driver_pay_type}
                                    onChange={e => handleInputChange('driver_pay_type', e.target.value)}
                                />
                                <Input
                                    type="text"
                                    label="Sub Type"
                                    value={formData.sub_type}
                                    onChange={e => handleInputChange('sub_type', e.target.value)}
                                />
                                <Input
                                    type="text"
                                    label="Reversal Type"
                                    value={formData.reversal_type}
                                    onChange={e => handleInputChange('reversal_type', e.target.value)}
                                />
                            </div>
                        )}

                        {/* Driver-specific fields */}
                        {type === 'driver' && (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <Input
                                    type="text"
                                    label="Sub Type"
                                    value={formData.sub_type}
                                    onChange={e => handleInputChange('sub_type', e.target.value)}
                                />
                                <Input
                                    type="text"
                                    label="Reversal Type"
                                    value={formData.reversal_type}
                                    onChange={e => handleInputChange('reversal_type', e.target.value)}
                                />
                                {formData.car_post && (
                                    <Input
                                        type="text"
                                        label="Car Post Type"
                                        value={formData.car_post_type}
                                        onChange={e => handleInputChange('car_post_type', e.target.value)}
                                        placeholder="e.g. Expense"
                                    />
                                )}
                            </div>
                        )}

                        {/* Checkboxes */}
                        <div className="flex flex-wrap gap-4 pt-2">
                            {type === 'car' && (
                                <label className="flex items-center text-gray-700">
                                    <input
                                        type="checkbox"
                                        className="mr-2 w-4 h-4 text-[#FF5733] border-gray-300 rounded focus:ring-[#FF5733]"
                                        checked={formData.driver_post}
                                        onChange={e => handleInputChange('driver_post', e.target.checked)}
                                    />
                                    Driver Post
                                </label>
                            )}
                            {type === 'car' && (
                                <label className="flex items-center text-gray-700">
                                    <input
                                        type="checkbox"
                                        className="mr-2 w-4 h-4 text-[#FF5733] border-gray-300 rounded focus:ring-[#FF5733]"
                                        checked={formData.can_split}
                                        onChange={e => handleInputChange('can_split', e.target.checked)}
                                    />
                                    Can Split
                                </label>
                            )}
                            {type === 'driver' && (
                                <label className="flex items-center text-gray-700">
                                    <input
                                        type="checkbox"
                                        className="mr-2 w-4 h-4 text-[#FF5733] border-gray-300 rounded focus:ring-[#FF5733]"
                                        checked={formData.car_post}
                                        onChange={e => handleInputChange('car_post', e.target.checked)}
                                    />
                                    Car Post
                                </label>
                            )}
                            <label className="flex items-center text-gray-700">
                                <input
                                    type="checkbox"
                                    className="mr-2 w-4 h-4 text-[#FF5733] border-gray-300 rounded focus:ring-[#FF5733]"
                                    checked={formData.def}
                                    onChange={e => handleInputChange('def', e.target.checked)}
                                />
                                Default
                            </label>
                            <label className="flex items-center text-gray-700">
                                <input
                                    type="checkbox"
                                    className="mr-2 w-4 h-4 text-[#FF5733] border-gray-300 rounded focus:ring-[#FF5733]"
                                    checked={formData.notify}
                                    onChange={e => handleInputChange('notify', e.target.checked)}
                                />
                                <div className="flex items-center gap-1">
                                    <FaBell className="text-[#FF5733]" />
                                    Notify
                                </div>
                            </label>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex justify-end gap-3 pt-3 border-t mt-4">
                            <Button
                                variant="outlined"
                                color="gray"
                                onClick={onClose}
                                className="flex items-center gap-2"
                            >
                                <FaTimes /> Cancel
                            </Button>
                            <Button
                                type="submit"
                                className="bg-[#FF5733] hover:bg-[#e64a2b] flex items-center gap-2"
                            >
                                <FaCheck /> Save Changes
                            </Button>
                        </div>
                    </form>
                </CardBody>
            </Card>
        </div>
    );
};

export default PaymentTypeEditModal; 